/**
 * What the workspace list shows when it has no rows to show.
 *
 * The drawing is the page's own subject rather than a stock empty box: two branch lines and the
 * node where a change would land, in the stroke language the row glyphs already use. Nothing
 * found leaves the branch unjoined — the gap is the whole picture.
 *
 * An empty list and an unread one look the same, so every state showing a host's answer offers
 * to ask for it again.
 */
import { RefreshCwIcon, SearchIcon } from "lucide-react";

import { Button } from "../ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from "../ui/empty";

/** A search long enough to be a paragraph is still a search, but it is not a title. */
function shortened(query: string): string {
  return query.length > 48 ? `${query.slice(0, 48)}…` : query;
}

/**
 * Drawn at the weight of the glyphs beside it rather than as an illustration with its own
 * palette, so an empty list reads as the same surface with nothing on it.
 */
function BranchMark() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 120 72"
      className="h-20 w-32 text-muted-foreground/60"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* The base line the change would land on, always whole. */}
      <path d="M10 58h100" className="text-muted-foreground/30" stroke="currentColor" />
      <circle cx="10" cy="58" r="5" fill="currentColor" fillOpacity={0.25} />
      <circle cx="110" cy="58" r="5" fill="currentColor" fillOpacity={0.25} />
      {/* A branch stopped short. What is missing is the join, so that is what the drawing
          withholds. */}
      <path d="M30 58c0-18 8-26 24-26h4" />
      <path
        d="M90 58c0-18-8-26-24-26h-4"
        strokeDasharray="2 7"
        className="text-muted-foreground/50"
      />
      <circle cx="60" cy="32" r={4} fill="none" className="text-muted-foreground/45" />
    </svg>
  );
}

export function PullRequestListEmptyState({
  query,
  filtered,
  onClearQuery,
  onRefresh,
}: {
  /** What was searched for, so the reader is told rather than left to guess. */
  readonly query: string;
  /** True when a state, involvement or project filter is narrowing the list. */
  readonly filtered: boolean;
  readonly onClearQuery?: (() => void) | undefined;
  readonly onRefresh: () => void;
}) {
  const refresh = (
    <Button size="sm" variant="outline" onClick={onRefresh}>
      <RefreshCwIcon aria-hidden className="size-3.5" />
      Check again
    </Button>
  );

  if (query.length > 0) {
    return (
      <Empty className="py-16">
        <BranchMark />
        <EmptyHeader>
          <EmptyTitle>Nothing matches “{shortened(query)}”</EmptyTitle>
          <EmptyDescription>
            Every Environment was searched for it. Try fewer words, or search by number, author or
            branch.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent className="flex-row flex-wrap justify-center gap-2">
          {onClearQuery === undefined ? null : (
            <Button size="sm" variant="outline" onClick={onClearQuery}>
              <SearchIcon className="size-3.5" />
              Clear search
            </Button>
          )}
          {/* The hosts answered this query once; a pull request opened since would answer
              differently, and nothing on screen says which of the two this is. */}
          {refresh}
        </EmptyContent>
      </Empty>
    );
  }

  return (
    <Empty className="py-16">
      <BranchMark />
      <EmptyHeader>
        <EmptyTitle>{filtered ? "Nothing under these filters" : "No Pull Requests"}</EmptyTitle>
        <EmptyDescription>
          {filtered
            ? "Widen the state, involvement or project filter to see more."
            : "Pull Requests from every AFAR Project in this workspace appear here."}
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent className="flex-row flex-wrap justify-center gap-2">{refresh}</EmptyContent>
    </Empty>
  );
}
